import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import TuneIcon from '@mui/icons-material/Tune'
import { Box, IconButton, InputBase, Tooltip, Typography } from '@mui/material'
import { useEffect, useState, type RefObject } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTokens } from '../theme'
import { AdvancedSearch } from './AdvancedSearch'

/** Mail search. Lives in the dock on desktop and in the top bar on mobile; `/` focuses it. */
export function SearchBox({ inputRef }: { inputRef: RefObject<HTMLInputElement | null> }) {
  const t = useTokens()
  const navigate = useNavigate()
  const location = useLocation()
  const [sp] = useSearchParams()
  const urlQuery = location.pathname === '/mail/s' ? sp.get('q') ?? '' : ''
  const [search, setSearch] = useState(urlQuery)
  const [focused, setFocused] = useState(false)
  const [advanced, setAdvanced] = useState<HTMLElement | null>(null)
  useEffect(() => setSearch(urlQuery), [urlQuery])
  const run = (q: string) => navigate(`/mail/s?q=${encodeURIComponent(q.trim())}`)

  return (
    <>
      <Box component="form" role="search" onSubmit={(e) => { e.preventDefault(); if (search.trim()) run(search) }}
        sx={{ display: 'flex', alignItems: 'center', height: 40, px: 0.5, borderRadius: '10px', bgcolor: 'background.paper', border: 1, borderColor: focused ? t.accent : 'divider', outline: focused ? `1px solid ${t.accent}` : 'none', transition: 'border-color 150ms, outline-color 150ms' }}>
        <IconButton type="submit" aria-label="Search" size="small"><SearchIcon fontSize="small" /></IconButton>
        <InputBase inputRef={inputRef} fullWidth value={search} onChange={(e) => setSearch(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          placeholder="Search mail" slotProps={{ input: { 'aria-label': 'Search mail' } }} sx={{ px: 0.5 }} />
        {search ? (
          <IconButton size="small" aria-label="Clear search" onClick={() => { setSearch(''); if (location.pathname === '/mail/s') navigate('/mail/f/inbox') }}><CloseIcon fontSize="small" /></IconButton>
        ) : !focused && (
          <Typography component="kbd" aria-hidden variant="caption" sx={{ fontFamily: 'inherit', fontWeight: 600, color: 'text.secondary', border: 1, borderColor: 'divider', borderRadius: '6px', px: 0.75, mr: 0.5, lineHeight: 1.5, display: { xs: 'none', md: 'inline' } }}>/</Typography>
        )}
        <Tooltip title="Search options"><IconButton size="small" aria-label="Search options" onClick={(e) => setAdvanced(e.currentTarget)}><TuneIcon fontSize="small" /></IconButton></Tooltip>
      </Box>
      <AdvancedSearch anchor={advanced} onClose={() => setAdvanced(null)} onSearch={run} initial={search} />
    </>
  )
}
